import React, { useState } from 'react';
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';

/**
 * Props interface for the ImageCropper component
 * @interface ImageCropperProps
 * @property {string} imageUrl - URL of the image to be cropped
 * @property {function} onCropComplete - Callback function that receives the cropped image as a Blob
 * @property {function} onCancel - Callback function triggered when cropping is cancelled
 */
interface ImageCropperProps {
  imageUrl: string;
  onCropComplete: (croppedImageBlob: Blob) => void;
  onCancel: () => void;
}

/**
 * A component that allows users to crop images using react-image-crop
 * @component
 * @param {ImageCropperProps} props - Component props
 * @returns {JSX.Element} Rendered ImageCropper component
 */
export const ImageCropper = ({
  imageUrl,
  onCropComplete,
  onCancel,
}: ImageCropperProps) => {
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 50,
    height: 50,
    x: 0,
    y: 0,
  });
  const [imageRef, setImageRef] = useState<HTMLImageElement | null>(null);

  /**
   * Handles the image load event and stores the image reference
   * @param {HTMLImageElement} img - The loaded image element
   * @returns {boolean} Always returns false to prevent default behavior
   */
  const onImageLoaded = (img: HTMLImageElement) => {
    setImageRef(img);
    return false;
  };

  /**
   * Creates a cropped version of the image and triggers the onCropComplete callback
   * Uses canvas to create a new image from the selected crop area
   */
  const getCroppedImg = () => {
    if (!imageRef) return;

    const canvas = document.createElement('canvas');
    const scaleX = imageRef.naturalWidth / imageRef.width;
    const scaleY = imageRef.naturalHeight / imageRef.height;

    // Type checking for crop dimensions
    const cropWidth = crop.width;
    const cropHeight = crop.height;
    const cropX = crop.x;
    const cropY = crop.y;

    if (
      cropWidth == null ||
      cropHeight == null ||
      cropX == null ||
      cropY == null
    )
      return;

    canvas.width = cropWidth;
    canvas.height = cropHeight;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    ctx.drawImage(
      imageRef,
      cropX * scaleX,
      cropY * scaleY,
      cropWidth * scaleX,
      cropHeight * scaleY,
      0,
      0,
      cropWidth,
      cropHeight,
    );

    canvas.toBlob((blob) => {
      if (blob) {
        onCropComplete(blob);
      }
    }, 'image/jpeg');
  };

  return (
    <div>
      <ReactCrop crop={crop} onChange={(c) => setCrop(c)} aspect={1}>
        <img
          src={imageUrl}
          onLoad={(e) => onImageLoaded(e.target as HTMLImageElement)}
          className="max-w-full h-auto"
        />
      </ReactCrop>
      <div className="mt-4 mb-4 space-x-4">
        <Button
          text="Crop"
          behavior={{
            type: 'onClick',
            onClick: getCroppedImg,
          }}
          variant="primary"
          size="small"
        />
        <Button
          text="Cancel"
          behavior={{
            type: 'onClick',
            onClick: onCancel,
          }}
          variant="outline"
          size="small"
        />
      </div>
    </div>
  );
};
